import type {
  DocumentAcceptedFormat,
  FieldDefinition,
  ValidationIssue,
} from '@inside-us-aos/domain';

import styles from './DocumentUpload.module.css';
import { issueMessage } from './issueMessage';

/**
 * Lightweight in-memory File metadata. Phase 5 wires real upload to
 * Cloud Storage and replaces the in-memory list with a list of
 * uploaded `documentId`s.
 */
export interface IUploadedFileMetadata {
  name: string;
  size: number;
  type: string;
}

export interface DocumentUploadProps {
  field: FieldDefinition;
  value: readonly IUploadedFileMetadata[];
  issues: readonly ValidationIssue[];
  onChange: (next: IUploadedFileMetadata[]) => void;
  /**
   * Whether the user has interacted with the field. When `false`,
   * issues are suppressed (no red dropzone, no error region, no
   * `aria-invalid`). Defaults to `true` for backward compatibility.
   */
  touched?: boolean;
  /** Fires on the input's native `onBlur`. */
  onBlur?: () => void;
}

const ACCEPT_BY_FORMAT: Record<DocumentAcceptedFormat, string> = {
  pdf: 'application/pdf',
  image: 'image/*',
  doc: 'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

function formatsToAccept(
  formats: readonly DocumentAcceptedFormat[] | undefined,
): string {
  if (!formats || formats.length === 0) return '';
  return formats.map(f => ACCEPT_BY_FORMAT[f]).join(',');
}

function isMultiUpload(field: FieldDefinition): boolean {
  const expected = field.documentSpec?.expectedCount;
  if (expected === undefined) return false;
  if (typeof expected === 'number') return expected > 1;
  // Range form: multi if max is undefined or > 1.
  return expected.max === undefined || expected.max > 1;
}

/**
 * Catalog-driven document-upload primitive. Reads `field.documentSpec`
 * for the explainer copy + accepted formats + expected count, renders
 * a drop-zone + native file input, and emits a list of File metadata
 * objects on change.
 *
 * Phase 2 holds the file in-memory; Phase 5 wires real upload to
 * Cloud Storage.
 */
export function DocumentUpload({
  field,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: DocumentUploadProps): JSX.Element {
  const inputId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${inputId}-errors`;
  const showIssues = touched && issues.length > 0;
  const dropzoneClassName = showIssues
    ? `${styles.field__dropzone} ${styles['field__dropzone--invalid']}`
    : styles.field__dropzone;

  const spec = field.documentSpec;
  const accept = formatsToAccept(spec?.acceptedFormats);
  const multiple = isMultiUpload(field);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const fileList = event.target.files;
    if (!fileList) {
      onChange([]);
      return;
    }
    const next: IUploadedFileMetadata[] = Array.from(fileList).map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    onChange(next);
  };

  return (
    <div className={styles.field} data-field-id={field.id}>
      <label htmlFor={inputId} className={styles.field__label}>
        {spec?.displayName ?? field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </label>

      {spec?.description !== undefined ? (
        <p className={styles.field__explainer}>{spec.description}</p>
      ) : null}

      {/*
        Wave 5 Slice 20 (2026-05-02) — wrap the native file input in a
        label-as-button. The native `<input type="file">` is UA-rendered
        and looks different on every OS/browser; hiding it via the
        sr-only recipe + styling the surrounding `<label>` as a clean
        button gives a consistent affordance ("Choose file" / "Replace
        file"). The label's `htmlFor` triggers the file picker on
        click. Native input semantics (keyboard focus, AT
        announcements, file picker) are unchanged.
      */}
      <div className={dropzoneClassName}>
        <input
          id={inputId}
          type="file"
          accept={accept || undefined}
          multiple={multiple}
          onChange={handleChange}
          onBlur={onBlur}
          aria-required={field.required === true || undefined}
          aria-invalid={showIssues || undefined}
          aria-describedby={showIssues ? errorListId : undefined}
          className={styles['field__file-input']}
        />
        <label
          htmlFor={inputId}
          className={styles['field__upload_button']}
        >
          <span aria-hidden="true" className={styles['field__upload_icon']}>
            ↑
          </span>
          <span>
            {value.length === 0
              ? multiple
                ? 'Choose files'
                : 'Choose file'
              : multiple
                ? 'Add or replace files'
                : 'Replace file'}
          </span>
        </label>

        {value.length > 0 ? (
          <ul
            className={styles['field__selected-list']}
            aria-label="Selected files"
            aria-live="polite"
          >
            {value.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className={styles['field__selected-row']}
              >
                <span
                  aria-hidden="true"
                  className={styles['field__selected-icon']}
                >
                  ✓
                </span>
                <span className={styles['field__selected-name']}>
                  {file.name}
                </span>
                <span className={styles['field__selected-size']}>
                  {Math.round(file.size / 1024)} KB
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles['field__dropzone_hint']}>
            {accept.includes('application/pdf')
              ? 'PDF, image, or Word document'
              : 'Image or document file'}
          </p>
        )}
      </div>

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {spec?.uscisReference !== undefined ? (
        <p className={styles.field__reference}>{spec.uscisReference}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          <ul id={errorListId} className={styles['field__error-list']}>
          {issues.map((issue, index) => (
            <li key={`${issue.code}-${index}`}>
              <span aria-hidden="true" className={styles['field__error-icon']}>
                !
              </span>
              {issueMessage(issue)}
            </li>
          ))}
        </ul>
        </div>
      ) : null}
    </div>
  );
}
